import { Locales, MUI_MAP_LOCALES } from '@/libs/i18n/settings';
import theme from '@/theme';
import { createTheme, ThemeProvider as ThemeProviderMui } from '@mui/material/styles';
import { createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const Context = createContext<{
    breakpointsStealPixels: number;
    setBreakpointsStealPixels: (value: number) => void;
} | null>(null);

const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const { i18n } = useTranslation();
    const [breakpointsStealPixels, setBreakpointsStealPixels] = useState(0);

    // Needed because MUI doesn't correctly handle the set mode at this level
    const mode = localStorage.getItem('mui-mode');
    const getSystemThemeMode = () =>
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

    const themeWithLocale = useMemo(
        () =>
            createTheme(
                {
                    ...theme,
                    defaultColorScheme: !mode ? getSystemThemeMode() : (mode as 'light' | 'dark'),
                    breakpoints: {
                        values: {
                            xs: 0 + breakpointsStealPixels,
                            sm: 600 + breakpointsStealPixels,
                            md: 900 + breakpointsStealPixels,
                            lg: 1200 + breakpointsStealPixels,
                            xl: 1536 + breakpointsStealPixels,
                        },
                    },
                },
                MUI_MAP_LOCALES[i18n.language as Locales],
            ), // Here we use "as" because the i18n.language must always be limited to the supportedLocales
        [i18n.language, breakpointsStealPixels, mode],
    );

    return (
        <Context.Provider value={{ breakpointsStealPixels, setBreakpointsStealPixels }}>
            <ThemeProviderMui theme={themeWithLocale} defaultMode={getSystemThemeMode()}>
                {children}
            </ThemeProviderMui>
        </Context.Provider>
    );
};
export default ThemeProvider;

export function useThemeProvider() {
    const currentContext = useContext(Context);
    if (!currentContext) {
        throw new Error('useThemeProvider must be used within a ThemeProvider');
    }
    return currentContext;
}
